// Browser Notifications helper.
//
// Notification API requires:
//   1. Page is served over HTTPS (or localhost on dev).
//   2. User has granted permission via Notification.requestPermission().
//
// We render the notification through the service worker (registered by
// VitePWA's `registerSW.js`) so the OS-level toast works even when the
// tab is backgrounded. If no SW is registered, we fall back to a
// `new Notification(...)` from the page context — works on Chrome but
// silently drops on Safari when backgrounded.
const PERMISSION_KEY = 'finance-app:notifications';

// Phase 208 / card 208-B2 — recurring-transaction reminder notifications.
// Two notification types, with DIFFERENT tags so one never replaces the
// other on screen:
//     Type 1 'recurring-reminder'     — the weekly "coming up" reminder
//     Type 2 'recurring-auto-added'   — the "added automatically" notice
// The rule selection is delegated to card 208-B1's computeRecurringReminders()
// and the category name comes from card 208-E2-era helpers, so no date
// arithmetic is re-implemented here.
import { computeRecurringReminders } from './recurringReminders.js';
import { categoryLabel } from './categories.js';

async function ensurePermission() {
  if (!('Notification' in window)) return 'unsupported';
  let perm = Notification.permission;
  if (perm === 'default') {
    try {
      perm = await Notification.requestPermission();
    } catch { perm = 'denied'; }
  }
  try { localStorage.setItem(PERMISSION_KEY, perm); } catch {}
  return perm;
}

function getPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

async function fire({ title, body, tag, url }) {
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'granted') return false;
  const opts = {
    body,
    tag,
    icon: '/icon.svg',
    badge: '/icon.svg',
    data: { url: url || '/' },
    requireInteraction: false,
  };
  // Prefer the service worker so notifications survive a backgrounded tab.
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.showNotification(title, opts);
        return true;
      }
    } catch { /* fall through */ }
  }
  try {
    new Notification(title, opts);
    return true;
  } catch { return false; }
}

// Click handler — when user clicks the toast, focus the tab + navigate.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener?.('message', (e) => {
    if (e.data?.type === 'navigate' && e.data.url) {
      window.focus();
      window.location.assign(e.data.url);
    }
  });
}


// Phase 56: Weekly recap builder + trigger.
// Builds the recap body from a week's transactions and fires a
// notification (only if user opted in via weekly_recap_enabled).
// Deduplicates via last_weekly_recap_iso stored in localStorage.

const RECAP_KEY = 'finance-app:last-weekly-recap-iso';

function getLastRecapIso() {
  try { return localStorage.getItem(RECAP_KEY) || null; } catch { return null; }
}
function setLastRecapIso(iso) {
  try { localStorage.setItem(RECAP_KEY, iso); } catch { /* ignore */ }
}

/**
 * Build a recap object from the last 7 days of transactions.
 * @returns { count, totalExpense, totalIncome, topCategory, topCategoryAmount, topCategoryName, vsLastWeekExpense }
 */
function buildWeeklyRecap(transactions, now = new Date()) {
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

  const thisWeek = transactions.filter(tx => {
    const d = new Date(tx.occurred_on);
    return d >= weekAgo && d <= now;
  });
  const lastWeek = transactions.filter(tx => {
    const d = new Date(tx.occurred_on);
    return d >= twoWeeksAgo && d < weekAgo;
  });

  const totalExpense = thisWeek
    .filter(tx => tx.type === 'expense')
    .reduce((s, tx) => s + (tx.amount || 0), 0);
  const totalIncome = thisWeek
    .filter(tx => tx.type === 'income')
    .reduce((s, tx) => s + (tx.amount || 0), 0);
  const lastWeekExpense = lastWeek
    .filter(tx => tx.type === 'expense')
    .reduce((s, tx) => s + (tx.amount || 0), 0);

  // Top expense category this week
  const byCategory = {};
  for (const tx of thisWeek) {
    if (tx.type !== 'expense') continue;
    const k = tx.category_name || '其他';
    byCategory[k] = (byCategory[k] || 0) + (tx.amount || 0);
  }
  const topEntry = Object.entries(byCategory).sort((a, b) => b[1] - a[1])[0] || [null, 0];

  const vsLastWeekExpense = lastWeekExpense === 0
    ? (totalExpense > 0 ? 'first' : 'same')
    : totalExpense > lastWeekExpense * 1.1
      ? 'up'
      : totalExpense < lastWeekExpense * 0.9
        ? 'down'
        : 'same';

  return {
    count: thisWeek.length,
    totalExpense,
    totalIncome,
    topCategory: topEntry[0],
    topCategoryAmount: topEntry[1],
    topCategoryName: topEntry[0],
    vsLastWeekExpense,
    lastWeekExpense,
  };
}

/**
 * Fire the weekly recap notification if it's been >7 days since the
 * last recap (or never fired) AND the user has opted in.
 *
 * @param opts.transactions  All transactions to summarize.
 * @param opts.currency      Currency symbol for formatting.
 * @param opts.formatMoney   (amount, currency) => string helper.
 * @param opts.t             i18n t() function.
 * @param opts.enabled       Boolean — opt-in toggle from settings.
 * @param opts.now           Optional Date (for testing).
 * @returns { fired: boolean, reason?: string, recap?: object }
 */
async function maybeFireWeeklyRecap({ transactions, currency, formatMoney, t, enabled, now = new Date() }) {
  if (!enabled) return { fired: false, reason: 'disabled' };
  const lastIso = getLastRecapIso();
  if (lastIso) {
    const days = (now.getTime() - new Date(lastIso).getTime()) / (24 * 60 * 60 * 1000);
    if (days < 7) return { fired: false, reason: 'too_soon', daysSinceLast: days };
  }
  if (!transactions || transactions.length === 0) {
    return { fired: false, reason: 'no_data' };
  }
  const recap = buildWeeklyRecap(transactions, now);
  if (recap.count === 0) {
    return { fired: false, reason: 'no_recent_tx', recap };
  }
  // Build notification body
  const bodyParts = [
    t('recap.expenseFmt', { amount: formatMoney(recap.totalExpense, currency) }),
  ];
  if (recap.topCategory) {
    bodyParts.push(t('recap.topCategoryFmt', {
      category: recap.topCategory,
      amount: formatMoney(recap.topCategoryAmount, currency),
    }));
  }
  if (recap.vsLastWeekExpense === 'up') {
    bodyParts.push(t('recap.upVsLastWeek'));
  } else if (recap.vsLastWeekExpense === 'down') {
    bodyParts.push(t('recap.downVsLastWeek'));
  }
  const body = bodyParts.join(' · ');
  const title = t('recap.title');
  const fired = await fire({ title, body, tag: 'weekly-recap', url: '/' });
  if (fired) setLastRecapIso(now.toISOString());
  return { fired, recap };
}

// Phase 67: Installment payment reminder.
// Fires when an installment's NEXT payment month matches the current month
// AND we haven't already nudged the user about this plan + month.
// Dedup key: 'installment-nudge:<plan_uuid>:<YYYY-MM>'.
const INSTALLMENT_NUDGE_PREFIX = 'finance-app:installment-nudge:';

function getLastInstallmentNudgeKey(planUuid, ym) {
  try { return localStorage.getItem(INSTALLMENT_NUDGE_PREFIX + planUuid + ':' + ym) || null; } catch { return null; }
}
function setLastInstallmentNudgeKey(planUuid, ym) {
  try { localStorage.setItem(INSTALLMENT_NUDGE_PREFIX + planUuid + ':' + ym, '1'); } catch { /* ignore */ }
}

/**
 * Fire a one-time reminder per (plan, month) when an installment payment
 * is due this month. Caller passes active plans + a computeInstallmentProgress
 * function (avoids circular import with lib/installments.js).
 *
 * @returns { fired: number, skipped: number }
 */
async function maybeFireInstallmentReminder({
  plans,
  creditCards,
  computeProgress,
  formatMoney,
  currency,
  t,
  enabled,
  now = new Date(),
}) {
  if (!enabled) return { fired: 0, skipped: 0 };
  if (!plans || plans.length === 0) return { fired: 0, skipped: 0 };
  const cardsByUuid = {};
  for (const c of (creditCards || [])) cardsByUuid[c.local_uuid] = c;
  const ymNow = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  let fired = 0;
  let skipped = 0;
  for (const plan of plans) {
    if (plan.ended || plan.deleted) { skipped++; continue; }
    const progress = computeProgress(plan, now);
    if (progress.is_complete || !progress.next_payment_month) { skipped++; continue; }
    if (progress.next_payment_month !== ymNow) { skipped++; continue; }
    if (getLastInstallmentNudgeKey(plan.local_uuid, ymNow)) { skipped++; continue; }
    const card = cardsByUuid[plan.credit_card_local_uuid];
    const title = t('installments.notify.title');
    const body = t('installments.notify.body', {
      card: card?.name || '',
      amount: formatMoney(plan.monthly_amount, currency),
      note: plan.note || '',
      period: progress.current_period_index + 1,
      total: plan.total_periods,
    });
    const ok = await fire({ title, body, tag: 'installment-' + plan.local_uuid, url: '/cards/' + plan.credit_card_local_uuid });
    if (ok) {
      setLastInstallmentNudgeKey(plan.local_uuid, ymNow);
      fired++;
    } else {
      skipped++;
    }
  }
  return { fired, skipped };
}

// ─── Phase 208 / card 208-B2 — recurring-transaction reminders ───────────
//
// WHY ONLY ONE NOTIFICATION PER WEEK
// -----------------------------------
// A user with six due recurring rules must get ONE toast listing all six,
// not six toasts. The dedup key is
//     finance-app:recurring-nudge:<ISO-week-start>
// so the reminder appears once per ISO week, not once per day.
//
// WHY `now` IS INJECTED
// ---------------------
// Every date decision is driven by the injected `now`, never by an implicit
// Date.now(). The week-start calculation, the rule selection and the
// rollover test all need a clock the test can control.
//
// WHY THE CATEGORY, NOT THE NOTE
// ------------------------------
// applyPending() (lib/localDb.js) returns `category_name` but NOT the
// rule's note -- it does not select the note column. So the notification
// names the category via categoryLabel() and says nothing about the note.
//
// DATE TRAPS
// ----------
// The week-start is computed on the LOCAL calendar and formatted from local
// fields -- never via toISOString(), which would shift the day in any
// negative-UTC-offset timezone (the Phase 207 bug). The rule selection
// itself is delegated to card 208-B1's computeRecurringReminders(), which
// already handles the local-calendar arithmetic correctly.

const RECURRING_NUDGE_PREFIX = 'finance-app:recurring-nudge';

function getLastRecurringNudgeKey(isoWeekStart) {
  try { return localStorage.getItem(`${RECURRING_NUDGE_PREFIX}:${isoWeekStart}`) || null; } catch { return null; }
}
function setLastRecurringNudgeKey(isoWeekStart) {
  try { localStorage.setItem(`${RECURRING_NUDGE_PREFIX}:${isoWeekStart}`, '1'); } catch { /* ignore */ }
}

/**
 * The local calendar date of the Monday that starts the ISO week containing
 * `d`, as 'YYYY-MM-DD'.
 *
 * Built from local calendar fields on purpose: toISOString() would shift the
 * day in any negative-UTC-offset timezone, so two users in different
 * timezones would land on different dedup keys for the same week.
 */
function isoWeekStart(d) {
  const day = d.getDay();               // 0 = Sunday
  const diff = (day + 6) % 7;           // days since Monday
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(monday.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayOfMonth}`;
}

/**
 * Fire ONE notification listing every recurring rule that is due soon.
 *
 * @param {Object} input
 * @param {Array<Object>} input.rules - recurring rules
 * @param {string} [input.currency] - display symbol, for formatMoney()
 * @param {Function} input.formatMoney - (amount, currency) => string
 * @param {Function} input.t - (key, vars, defaultValue) => string
 * @param {boolean} input.enabled - the global master switch
 * @param {Date} [input.now] - the injected clock
 * @param {number} [input.horizonDays=7] - how far ahead to look
 * @returns {Promise<{fired: boolean, reason?: string, rules?: Array}>}
 */
async function maybeFireRecurringReminder({
  rules = [],
  currency,
  formatMoney,
  t,
  enabled,
  now = new Date(),
  horizonDays = 7,
} = {}) {
  // Gate order matters: the cheap checks first, so a disabled reminder never
  // touches the clock, the storage or the rules.
  if (!enabled) return { fired: false, reason: 'disabled' };
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return { fired: false, reason: 'no_permission' };
  }

  const weekStart = isoWeekStart(now);
  if (getLastRecurringNudgeKey(weekStart)) {
    return { fired: false, reason: 'already_nudged' };
  }

  let due = [];
  try {
    due = computeRecurringReminders({ rules, today: now, horizonDays });
  } catch {
    // A malformed rule must not take the dashboard down. Treat it as
    // "nothing due" rather than throwing from a notification path.
    due = [];
  }
  if (!due || due.length === 0) return { fired: false, reason: 'no_due_rules' };

  const lines = due.map(({ rule, daysUntil }) => {
    const when = daysUntil === 0
      ? t('recurring.reminder.dueToday', undefined, 'Due today')
      : t('recurring.reminder.dueInDays', { n: daysUntil }, `Due in ${daysUntil} days`);
    const category = categoryLabel({ name: rule.category_name }, t);
    const amount = typeof formatMoney === 'function'
      ? formatMoney(rule.amount, currency)
      : '';
    return t('recurring.reminder.itemFmt', { category, amount, when },
             `${category} · ${amount} · ${when}`);
  });

  const title = t('recurring.reminder.title', undefined, 'Reminder');
  const body = t('recurring.reminder.countFmt', { count: due.length },
                 `${due.length} upcoming fixed transactions`);

  const ok = await fire({
    title,
    // The count is the headline; the per-rule lines are the detail. The
    // Notification API has no separate body/subtitle split, so the body
    // carries both.
    body: lines.length ? `${body}\n${lines.join('\n')}` : body,
    tag: 'recurring-reminder',
    url: '/settings',
  });

  // Only record the dedup key when the notification actually went out --
  // otherwise a failed fire would suppress the reminder for the whole week.
  if (ok) setLastRecurringNudgeKey(weekStart);
  return { fired: ok, rules: due };
}

// At most once per SESSION. applyPending() is gated by a 60-second cache
// (lib/localDb.js), so a second load inside the same minute has nothing new
// to report; a module-level flag matches that lifetime.
let autoAppliedNotified = false;

/** Reset the once-per-session flag. Exported for tests and for a hard reset. */
function resetAutoAppliedNotice() {
  autoAppliedNotified = false;
}

/**
 * Fire ONE notification when recurring transactions were added
 * automatically. Fires at most once per session, not once per rule.
 *
 * @param {Object} input
 * @param {Array<Object>} input.applied - rows from applyPending(); each has
 *   `category_name`, `amount` and `type`
 * @param {string} [input.currency]
 * @param {Function} input.formatMoney - (amount, currency) => string
 * @param {Function} input.t - (key, vars, defaultValue) => string
 * @param {boolean} input.enabled
 * @returns {Promise<{fired: boolean, reason?: string}>}
 */
async function maybeFireAutoAppliedReminder({
  applied = [],
  currency,
  formatMoney,
  t,
  enabled,
} = {}) {
  if (!enabled) return { fired: false, reason: 'disabled' };
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return { fired: false, reason: 'no_permission' };
  }
  if (!applied || applied.length === 0) return { fired: false, reason: 'nothing_applied' };
  if (autoAppliedNotified) return { fired: false, reason: 'already_notified' };

  // Name the CATEGORY -- applyPending() does not return the rule's note.
  const name = categoryLabel({ name: applied[0].category_name }, t);
  const amount = typeof formatMoney === 'function'
    ? formatMoney(applied[0].amount, currency)
    : '';
  const count = applied.length;

  const title = t('recurring.reminder.title', undefined, 'Reminder');
  const body = t('recurring.reminder.appliedFmt', { name, amount, count },
                 `${name} was added automatically`);

  const ok = await fire({
    title,
    body,
    tag: 'recurring-auto-added',
    url: '/',
  });

  if (ok) autoAppliedNotified = true;
  return { fired: ok, count };
}

export { maybeFireRecurringReminder, maybeFireAutoAppliedReminder, resetAutoAppliedNotice, isoWeekStart, getLastRecurringNudgeKey };

export default { ensurePermission, getPermission, fire, buildWeeklyRecap, maybeFireWeeklyRecap, maybeFireInstallmentReminder, getLastRecapIso, getLastInstallmentNudgeKey, maybeFireRecurringReminder, maybeFireAutoAppliedReminder, resetAutoAppliedNotice, getLastRecurringNudgeKey };